import React from 'react';
import { Canvas } from '@react-three/fiber';
import { PerspectiveCamera, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { CharacterCustomization } from '@/types/game';

const Character3D: React.FC<{ customization: CharacterCustomization }> = ({ customization }) => {
  const skinColorMap: Record<string, string> = {
    'Coco Brown': '#6B4423',
    'Ebony': '#2C2C2C',
    'Peachy Pale': '#F4D4C8',
    'Tan': '#C89968',
    'Pale Ivory': '#F5E6D3',
    'Deep Plum': '#4A3A5C',
    'Golden Bronze': '#B8860B',
    'Cool Gray': '#A9A9A9',
  };

  const hairColorMap: Record<string, string> = {
    'Pale Teal': '#7FFFD4',
    'Blonde': '#FFD700',
    'Black': '#000000',
    'Dark Brown': '#3E2723',
    'Deep Purple': '#663399',
    'Crimson Red': '#DC143C',
    'Silver': '#C0C0C0',
    'Midnight Blue': '#191970',
  };

  const eyeColorMap: Record<string, string> = {
    'Amber': '#FFBF00',
    'Violet': '#EE82EE',
    'Emerald': '#50C878',
    'Ice Blue': '#B0E0E6',
    'Gold': '#FFD700',
    'Deep Black': '#1C1C1C',
    'Silver': '#C0C0C0',
  };

  const skinColor = skinColorMap[customization.skinColor] || '#6B4423';
  const hairColor = hairColorMap[customization.hairColor] || '#FFD700';
  const eyeColor = eyeColorMap[customization.eyeColor] || '#FFBF00';

  return (
    <group>
      {/* Head */}
      <mesh position={[0, 1.2, 0]}>
        <sphereGeometry args={[0.4, 32, 32]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Hair */}
      <mesh position={[0, 1.65, 0]}>
        {customization.hairStyle === 'locs' && <cylinderGeometry args={[0.25, 0.3, 0.8, 16]} />}
        {customization.hairStyle === 'wavy-long' && <coneGeometry args={[0.35, 1, 32]} />}
        {customization.hairStyle === 'pixie' && <boxGeometry args={[0.4, 0.4, 0.3]} />}
        {customization.hairStyle === 'braids' && <cylinderGeometry args={[0.3, 0.3, 0.9, 8]} />}
        {customization.hairStyle === 'straight-long' && <coneGeometry args={[0.32, 1.2, 32]} />}
        {customization.hairStyle === 'curly' && <sphereGeometry args={[0.45, 16, 16]} />}
        {customization.hairStyle === 'spiky' && <coneGeometry args={[0.4, 0.6, 8]} />}
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Eyes */}
      <mesh position={[-0.12, 1.35, 0.35]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color={eyeColor} />
      </mesh>
      <mesh position={[0.12, 1.35, 0.35]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color={eyeColor} />
      </mesh>

      {/* Body */}
      <mesh position={[0, 0.4, 0]}>
        <boxGeometry args={[0.3, 0.8, 0.2]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Arms */}
      <mesh position={[-0.25, 0.6, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.6, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.25, 0.6, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.6, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Legs */}
      <mesh position={[-0.1, -0.3, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.8, 16]} />
        <meshStandardMaterial color="#2C2C2C" />
      </mesh>
      <mesh position={[0.1, -0.3, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.8, 16]} />
        <meshStandardMaterial color="#2C2C2C" />
      </mesh>
    </group>
  );
};

interface CharacterPreview3DProps {
  customization: CharacterCustomization;
}

export const CharacterPreview3D: React.FC<CharacterPreview3DProps> = ({ customization }) => {
  return (
    <Canvas camera={{ position: [0, 0.5, 2], fov: 50 }}>
      <PerspectiveCamera makeDefault position={[0, 0.5, 2]} />
      <ambientLight intensity={0.7} />
      <pointLight position={[5, 5, 5]} intensity={0.8} />
      <Character3D customization={customization} />
      <OrbitControls autoRotate autoRotateSpeed={4} />
    </Canvas>
  );
};
